import React, { useState, useMemo } from 'react';
import { View, FlatList, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { Text, TextInput, Button, Card, Searchbar } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { useGetAmCategoriesQuery } from '../redux/services/amcategoryApi';
import {
  useGetAmProductsQuery,
  useSaveAmProductMutation,
  useDeleteAmProductMutation,
  type AmProduct
} from '../redux/services/amproductApi';
import { ShowToast } from '../Common/common';

import T from '../Components/T';
import { tp } from '../utils/translateText';
/** Asset product master: list, add/edit, delete, and seed the standard hotel set. */
const AmProductList = ({ navigation }: any) => {
  const company = useSelector((state: RootState) => state.gdata?.company);
  const { data: products = [], isLoading } = useGetAmProductsQuery();
  const { data: categories = [] } = useGetAmCategoriesQuery(company || '', { skip: !company });
  const [saveProduct, { isLoading: isSaving }] = useSaveAmProductMutation();
  const [deleteProduct] = useDeleteAmProductMutation();

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState<number | null>(null);
  const [editing, setEditing] = useState<Partial<AmProduct> | null>(null);
  const [catOpen, setCatOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter(p => {
      if (catFilter && Number(p.pt_category) !== catFilter) return false;
      return !q || [p.pt_code, p.pt_name, p.pt_brand, p.pt_model].some(v => (v || '').toLowerCase().includes(q));
    });
  }, [products, search, catFilter]);

  const save = async () => {
    if (!editing?.pt_code?.trim() || !editing?.pt_name?.trim()) {
      ShowToast('Product code and name are required', 'error');
      return;
    }
    try {
      await saveProduct({
        pt_id: editing.pt_id || 0,
        pt_code: editing.pt_code.trim(),
        pt_name: editing.pt_name.trim(),
        pt_category: editing.pt_category ?? null,
        pt_brand: editing.pt_brand,
        pt_model: editing.pt_model,
        pt_cost: editing.pt_cost,
        pt_depmethod: editing.pt_depmethod || 'S',
        pt_deprate: editing.pt_deprate,
        pt_usefullife: editing.pt_usefullife,
        pt_warranty_months: editing.pt_warranty_months,
        pt_uom: editing.pt_uom || 'NOS',
        pt_active: '1'
      } as AmProduct).unwrap();
      ShowToast('Product saved');
      setEditing(null);
      setCatOpen(false);
    } catch (e: any) {
      ShowToast(e?.data?.message || 'Could not save product', 'error');
    }
  };

  const confirmDelete = (p: AmProduct) =>
    Alert.alert(tp("Delete product"), `Delete "${p.pt_name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteProduct(p.pt_id as number).unwrap();
          } catch (e: any) {
            ShowToast(e?.data?.message || 'Could not delete (product may be in use)', 'error');
          }
        }
      }
    ]);

  const num = (v: string) => (v ? parseFloat(v) || 0 : undefined);
  const catName = (id?: number | null) => categories.find(c => Number(c.ct_id) === Number(id))?.ct_name;

  return (
    <View style={styles.container}>
      <Searchbar placeholder={tp("Search code, name, brand, model")} value={search} onChangeText={setSearch} style={styles.search} />
      {categories.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0, flexShrink: 0, height: 40 }} contentContainerStyle={styles.chips}>
          <TouchableOpacity style={[styles.chip, !catFilter && styles.chipOn]} onPress={() => setCatFilter(null)}>
            <Text style={!catFilter ? styles.chipTextOn : styles.chipText}><T>All</T></Text>
          </TouchableOpacity>
          {categories.map(c => (
            <TouchableOpacity
              key={String(c.ct_id)}
              style={[styles.chip, catFilter === Number(c.ct_id) && styles.chipOn]}
              onPress={() => setCatFilter(catFilter === Number(c.ct_id) ? null : Number(c.ct_id))}
            >
              <Text style={catFilter === Number(c.ct_id) ? styles.chipTextOn : styles.chipText}>{c.ct_name}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.pill} onPress={() => setEditing({ pt_depmethod: 'S', pt_uom: 'NOS' })}>
          <Icon name="plus" size={12} color="#fff" />
          <Text style={styles.pillText}> <T>New product</T></Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.pill, styles.pillAlt]} onPress={() => navigation.navigate('AmSeedCatalogue')}>
          <Icon name="magic" size={12} color="#2E7D32" />
          <Text style={[styles.pillText, { color: '#2E7D32' }]}> <T>Seed from template</T></Text>
        </TouchableOpacity>
      </View>

      {editing && (
        <ScrollView style={{ maxHeight: 420 }}>
          <Card style={styles.editor}>
            <TextInput label="Code *" value={editing.pt_code || ''} onChangeText={v => setEditing({ ...editing, pt_code: v })} mode="outlined" autoCapitalize="characters" style={styles.input} />
            <TextInput label="Name *" value={editing.pt_name || ''} onChangeText={v => setEditing({ ...editing, pt_name: v })} mode="outlined" style={styles.input} />
            <TouchableOpacity style={styles.select} onPress={() => setCatOpen(!catOpen)}>
              <Text>{catName(editing.pt_category) || 'Category'}</Text>
            </TouchableOpacity>
            {catOpen && (
              <ScrollView style={styles.dropdown} nestedScrollEnabled>
                {categories.map(c => (
                  <TouchableOpacity
                    key={String(c.ct_id)}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setEditing({ ...editing, pt_category: Number(c.ct_id) });
                      setCatOpen(false);
                    }}
                  >
                    <Text>{c.ct_name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <TextInput label="Brand" value={editing.pt_brand || ''} onChangeText={v => setEditing({ ...editing, pt_brand: v })} mode="outlined" style={styles.input} />
            <TextInput label="Model" value={editing.pt_model || ''} onChangeText={v => setEditing({ ...editing, pt_model: v })} mode="outlined" style={styles.input} />
            <View style={styles.rowInputs}>
              <TextInput label="Std. cost" value={editing.pt_cost != null ? String(editing.pt_cost) : ''} onChangeText={v => setEditing({ ...editing, pt_cost: num(v) })} keyboardType="decimal-pad" mode="outlined" style={[styles.input, styles.half]} />
              <TextInput label="Useful life (yrs)" value={editing.pt_usefullife != null ? String(editing.pt_usefullife) : ''} onChangeText={v => setEditing({ ...editing, pt_usefullife: num(v) })} keyboardType="numeric" mode="outlined" style={[styles.input, styles.half]} />
            </View>
            <TextInput label="Warranty (months)" value={editing.pt_warranty_months != null ? String(editing.pt_warranty_months) : ''} onChangeText={v => setEditing({ ...editing, pt_warranty_months: num(v) })} keyboardType="numeric" mode="outlined" style={styles.input} />
            <View style={styles.editorBtns}>
              <Button mode="contained" onPress={save} loading={isSaving} disabled={isSaving}>
                Save
              </Button>
              <Button mode="outlined" onPress={() => { setEditing(null); setCatOpen(false); }} disabled={isSaving}>
                Cancel
              </Button>
            </View>
          </Card>
        </ScrollView>
      )}

      {isLoading ? (
        <ActivityIndicator size="large" color="#2E7D32" style={{ flex: 1 }} />
      ) : filtered.length === 0 ? (
        <View style={styles.empty}>
          <Icon name="cubes" size={50} color="#ccc" />
          <Text style={styles.emptyText}><T>No products yet - tap "Seed from template"</T></Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={p => String(p.pt_id)}
          contentContainerStyle={{ padding: 10 }}
          renderItem={({ item }) => (
            <View style={styles.item}>
              <TouchableOpacity style={{ flex: 1 }} onPress={() => setEditing(item)}>
                <Text style={styles.title}>{item.pt_name}</Text>
                <Text style={styles.sub}>
                  {item.pt_code}
                  {item.category_name || catName(item.pt_category) ? `  ·  ${item.category_name || catName(item.pt_category)}` : ''}
                </Text>
                {item.pt_brand || item.pt_model ? <Text style={styles.sub}>{[item.pt_brand, item.pt_model].filter(Boolean).join(' ')}</Text> : null}
              </TouchableOpacity>
              <TouchableOpacity onPress={() => confirmDelete(item)} style={styles.del}>
                <Icon name="trash" size={18} color="#d32f2f" />
              </TouchableOpacity>
            </View>
          )}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  search: { margin: 10, marginBottom: 6 },
  chips: { paddingHorizontal: 10, gap: 6, alignItems: 'center' },
  chip: { borderWidth: 1, borderColor: '#2E7D32', borderRadius: 14, paddingHorizontal: 10, paddingVertical: 4, marginRight: 6, backgroundColor: '#fff' },
  chipOn: { backgroundColor: '#2E7D32' },
  chipText: { fontSize: 12, color: '#2E7D32' },
  chipTextOn: { fontSize: 12, color: '#fff' },
  actions: { flexDirection: 'row', gap: 8, marginHorizontal: 10, marginVertical: 6 },
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#2E7D32', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 6 },
  pillAlt: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#2E7D32' },
  pillText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  editor: { margin: 10, padding: 12 },
  input: { marginBottom: 8 },
  rowInputs: { flexDirection: 'row', gap: 8 },
  half: { flex: 1 },
  select: { borderWidth: 1, borderColor: '#ddd', borderRadius: 4, padding: 12, backgroundColor: '#fafafa', marginBottom: 8 },
  dropdown: { maxHeight: 160, borderWidth: 1, borderColor: '#ddd', borderRadius: 4, backgroundColor: '#fff', marginTop: -4, marginBottom: 8 },
  dropdownItem: { padding: 12, borderBottomWidth: 1, borderBottomColor: '#eee' },
  editorBtns: { flexDirection: 'row', gap: 10 },
  item: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8, borderLeftWidth: 4, borderLeftColor: '#2E7D32' },
  title: { fontSize: 14, fontWeight: 'bold' },
  sub: { fontSize: 12, color: '#666', marginTop: 2 },
  del: { padding: 8 },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  emptyText: { marginTop: 10, fontSize: 14, color: '#999', textAlign: 'center' }
});

export default AmProductList;
